import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getRuntimeWsBase } from "@/utils/runtimeApi";

const WebSocketContext = createContext(null);

const WS_URL = getRuntimeWsBase();

const EVENT_ALIASES = {
  NEW_ALERT: ["new_alert"],
  NEW_MESSAGE: ["new_message"],
};

export function WebSocketProvider({ children }) {
  const { user } = useAuth();
  const ws = useRef(null);
  const [connected, setConnected] = useState(false);
  const listeners = useRef({});
  const reconnectTimer = useRef(null);

  const on = useCallback((event, callback) => {
    if (!listeners.current[event]) {
      listeners.current[event] = [];
    }
    listeners.current[event].push(callback);
    return () => {
      listeners.current[event] = listeners.current[event].filter(cb => cb !== callback);
    };
  }, []);

  const emit = useCallback((event, data) => {
    const cbs = listeners.current[event] || [];
    cbs.forEach(cb => cb(data));
  }, []);

  const connect = useCallback(() => {
    if (!user) return;
    const token = localStorage.getItem("hana_access_token");
    if (!token) return;

    const wsUrl = `${WS_URL}/api/ws/${user.id}?token=${token}`;
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      setConnected(true);
      ws.current = socket;
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        emit(data.type, data);
        (EVENT_ALIASES[data.type] || []).forEach((alias) => emit(alias, data));
        emit("*", data);
      } catch (e) {
        // ignore non-JSON messages
      }
    };

    socket.onclose = () => {
      setConnected(false);
      ws.current = null;
      // Reconnect after 5s
      if (user) {
        reconnectTimer.current = setTimeout(connect, 5000);
      }
    };

    socket.onerror = (err) => {
      console.warn("WebSocket error:", err);
      socket.close();
    };

    // Ping every 30s
    const pingInterval = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send("ping");
      }
    }, 30000);

    socket._pingInterval = pingInterval;
  }, [user, emit]);

  useEffect(() => {
    if (user) {
      connect();
    } else {
      if (ws.current) {
        clearInterval(ws.current._pingInterval);
        ws.current.close();
      }
      clearTimeout(reconnectTimer.current);
    }
    return () => {
      clearTimeout(reconnectTimer.current);
      if (ws.current) {
        clearInterval(ws.current._pingInterval);
        ws.current.close();
      }
    };
  }, [user, connect]);

  return (
    <WebSocketContext.Provider value={{ connected, on }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  return useContext(WebSocketContext);
}
